// SUB-OFFICE 7 — physical task specs. Seeded generators shared by the client
// renderer and the worker verifier: both sides derive the identical task from
// (seed, grade_tier), so the server can recompute every claim from scratch.
// W1 ships FORM 5-J / 2-V / 33-C / 0-0; W2 systems register here later.

import { fnv1a, intSeeded, pickSeeded, streamFrom } from './rng';

// ---------------------------------------------------------------------------
// System registry — the docket composer weights only implemented systems.
// ---------------------------------------------------------------------------

export type PhysicalSystemId =
  | 'FORM5D'
  | 'FORM5J'
  | 'FORM5S'
  | 'FORM5M'
  | 'FORM17O'
  | 'FORM33C'
  | 'FORM8R'
  | 'FORM12K'
  | 'FORM4T'
  | 'FORM2V'
  | 'FORM44S'
  | 'FORM00';

export type WeightClass = 'HEADLINER' | 'MID_BENCH' | 'SHOWPIECE' | 'FILLER' | 'RARE_PRESTIGE' | 'CIVIC' | 'CAGED' | 'NON_DOCKET';

export interface PhysicalSystemDef {
  system: PhysicalSystemId;
  form_no: string;
  title: string;
  station: string; // where in the building the client stages it
  tradition: 'CLERKSHIP' | 'CONVEYANCE' | 'MAINTENANCE' | 'CEREMONY';
  weight_class: WeightClass;
  weight: number;
  min_grade: number;
  cooldown_sessions: number;
  duration_est_sec: number;
  act_noun: string; // for template memos: 'the relief of the junction'
  implemented: boolean; // composer weights only implemented systems
}

export const PHYSICAL_SYSTEMS: PhysicalSystemDef[] = [
  // ---- W1 (implemented) ----
  {
    system: 'FORM5J',
    form_no: 'FORM 5-J',
    title: 'CONCURRENCE DUTY',
    station: 'WINDOW ROW',
    tradition: 'CLERKSHIP',
    weight_class: 'FILLER',
    weight: 1,
    min_grade: 0,
    cooldown_sessions: 0,
    duration_est_sec: 90,
    act_noun: 'the concurrence shift',
    implemented: true,
  },
  {
    system: 'FORM2V',
    form_no: 'FORM 2-V',
    title: 'ADJUDICATION PRESSURE ATTENDANCE',
    station: 'VAULT FACE',
    tradition: 'CEREMONY',
    weight_class: 'SHOWPIECE',
    weight: 2,
    min_grade: 0,
    cooldown_sessions: 3,
    duration_est_sec: 60,
    act_noun: 'the attendance of the pressure',
    implemented: true,
  },
  {
    system: 'FORM33C',
    form_no: 'FORM 33-C',
    title: 'THE ROUTE OF CONTRITION',
    station: 'THE FLOOR',
    tradition: 'CONVEYANCE',
    weight_class: 'CAGED', // never composed into routine slots
    weight: 0,
    // Penance is assigned after a DENIED band at ANY grade (spec-physical-verbs
    // 33-C: role-locked to penance/election, never gated by grade). min_grade 0
    // so no consumer treats the registry as a grade gate; the difficulty ladder
    // still starts at tier 1 via the composer's Math.max(1, gIdx) clamp.
    min_grade: 0,
    cooldown_sessions: 0,
    duration_est_sec: 160,
    act_noun: 'the circuit of contrition',
    implemented: true,
  },
  {
    system: 'FORM00',
    form_no: 'FORM 0-0',
    title: 'EXERCISE ZERO',
    station: 'ROOM 0',
    tradition: 'CEREMONY',
    weight_class: 'NON_DOCKET', // seeded event only
    weight: 0,
    min_grade: 2,
    cooldown_sessions: 6,
    duration_est_sec: 60,
    act_noun: 'the exercise that is not occurring',
    implemented: true,
  },
  // ---- W2 (implemented server-side; the composer now weights them) ----
  { system: 'FORM5D', form_no: 'FORM 5-D', title: 'THE DISCREPANCY WINDOW', station: 'WINDOW ROW', tradition: 'CLERKSHIP', weight_class: 'HEADLINER', weight: 3, min_grade: 0, cooldown_sessions: 1, duration_est_sec: 120, act_noun: 'the inspection of documents', implemented: true },
  { system: 'FORM5M', form_no: 'FORM 5-M', title: 'MANUAL CONVEYANCE DUTY', station: 'THE FLOOR', tradition: 'CONVEYANCE', weight_class: 'HEADLINER', weight: 3, min_grade: 1, cooldown_sessions: 1, duration_est_sec: 100, act_noun: 'the conveyance of the parcel', implemented: true },
  { system: 'FORM8R', form_no: 'FORM 8-R', title: 'JUNCTION RELIEF DUTY', station: 'ROTUNDA TRUNK', tradition: 'MAINTENANCE', weight_class: 'HEADLINER', weight: 3, min_grade: 1, cooldown_sessions: 1, duration_est_sec: 120, act_noun: 'the relief of the junction', implemented: true },
  { system: 'FORM5S', form_no: 'FORM 5-S', title: 'RETENTION DUTY', station: 'COPIER ROOM', tradition: 'CLERKSHIP', weight_class: 'MID_BENCH', weight: 2, min_grade: 1, cooldown_sessions: 2, duration_est_sec: 120, act_noun: 'the retention shift', implemented: true },
  { system: 'FORM4T', form_no: 'FORM 4-T', title: 'TERMINAL SUPPORT ROUNDS', station: 'ARCHIVE WALL', tradition: 'MAINTENANCE', weight_class: 'MID_BENCH', weight: 2, min_grade: 1, cooldown_sessions: 2, duration_est_sec: 110, act_noun: 'the support of the unit', implemented: true },
  { system: 'FORM12K', form_no: 'FORM 12-K', title: 'CEREMONIAL ARMATURE ALIGNMENT', station: 'WINDOW ROW', tradition: 'MAINTENANCE', weight_class: 'MID_BENCH', weight: 2, min_grade: 1, cooldown_sessions: 2, duration_est_sec: 135, act_noun: 'the alignment of the armature', implemented: true },
  { system: 'FORM17O', form_no: 'FORM 17-O', title: 'OBSERVATION DUTY', station: 'SECURITY WICKET', tradition: 'CONVEYANCE', weight_class: 'RARE_PRESTIGE', weight: 1, min_grade: 2, cooldown_sessions: 3, duration_est_sec: 180, act_noun: 'the observation of the subject', implemented: true },
  // 44-S is CIVIC-slot only per spec (never a routine physical slot): the
  // registry carries it with weight_class CIVIC so routineSystems() skips it
  // and the composer's civic rotation reaches for it directly.
  { system: 'FORM44S', form_no: 'FORM 44-S', title: 'STATIONARY COMPOSURE CERTIFICATION', station: 'THE FLOOR', tradition: 'CEREMONY', weight_class: 'CIVIC', weight: 2, min_grade: 0, cooldown_sessions: 2, duration_est_sec: 60, act_noun: 'the certification of composure', implemented: true },
];

export function systemDef(id: PhysicalSystemId): PhysicalSystemDef {
  const def = PHYSICAL_SYSTEMS.find((s) => s.system === id);
  if (!def) throw new Error(`unknown physical system ${id}`);
  return def;
}

/** Routine-slot candidates: implemented, weighted, not caged/civic/non-docket. */
export function routineSystems(): PhysicalSystemDef[] {
  return PHYSICAL_SYSTEMS.filter(
    (s) =>
      s.implemented &&
      s.weight_class !== 'CAGED' &&
      s.weight_class !== 'NON_DOCKET' &&
      s.weight_class !== 'CIVIC'
  );
}

// ---------------------------------------------------------------------------
// FORM 5-J — CONCURRENCE DUTY
// The tray itself is composed server-side from the live ledger (stratified,
// backfilled from seeds); the spec here carries the shape both sides render.
// ---------------------------------------------------------------------------

export interface Form5JSpec {
  system: 'FORM5J';
  seed: string;
  grade_tier: number;
  tray_size: number; // 5 (grade 0-1) or 7 (grade 2+)
  tray: string[]; // ledger ids, server-filled at issue time
  placard_variant: number; // 0: extremes only; 1: 'THE BUREAU TOLERATES...'
  tolerated_is_approve: boolean; // pinned rule from grade 2
}

export function gen5J(seed: string, gradeTier: number): Form5JSpec {
  const size = gradeTier >= 2 ? 7 : 5;
  return {
    system: 'FORM5J',
    seed,
    grade_tier: gradeTier,
    tray_size: size,
    tray: [], // server fills from the live ledger at issue time
    placard_variant: gradeTier >= 2 ? 1 : 0,
    tolerated_is_approve: gradeTier >= 2,
  };
}

// ---------------------------------------------------------------------------
// FORM 2-V — ADJUDICATION PRESSURE ATTENDANCE
// Needles drift on seeded curves; the full sim is a pure function of
// (spec, input events), so the server replays it from inputs alone.
// ---------------------------------------------------------------------------

export interface DriftComponent {
  a: number; // amplitude (velocity units/s)
  f: number; // frequency Hz
  p: number; // phase rad
}

export interface LurchEvent {
  t_ms: number;
  gauge: number;
  mag: number; // instantaneous position step (+/-)
}

export interface Form2VSpec {
  system: 'FORM2V';
  seed: string;
  grade_tier: number;
  gauges: number; // 2-4
  band_width: number; // in-band = |x - 0.5| <= band_width/2
  duration_s: number; // 45-75
  drift: DriftComponent[][]; // per gauge
  lurches: LurchEvent[];
  deliberation: { designation: string; genre: string };
}

export const GAUGE_BLEED_RATE = 0.14; // position units/s while bleed held
export const GAUGE_FEED_KICK = 0.055; // instantaneous position step per tap
export const GAUGE_SIM_DT_MS = 50;

const GHOST_GENRES = ['A CONFESSION, CATEGORY C', 'A DENUNCIATION', 'A GRATITUDE AUDIT', 'A PREEMPTIVE APOLOGY', 'WAITING-ROOM TESTIMONY'];

export function gen2V(seed: string, gradeTier: number): Form2VSpec {
  const rnd = streamFrom(seed + ':2v');
  const gauges = gradeTier <= 0 ? 2 : gradeTier >= 4 ? 4 : 3;
  const bandWidth = gradeTier <= 0 ? 0.40 : gradeTier === 1 ? 0.34 : gradeTier >= 4 ? 0.24 : 0.28;
  const duration = 45 + Math.floor(rnd() * 31); // 45-75
  const drift: DriftComponent[][] = [];
  for (let g = 0; g < gauges; g++) {
    const comps: DriftComponent[] = [];
    for (let c = 0; c < 3; c++) {
      comps.push({
        a: 0.02 + rnd() * 0.05 * (1 + gradeTier * 0.15),
        f: 0.05 + rnd() * 0.22,
        p: rnd() * Math.PI * 2,
      });
    }
    drift.push(comps);
  }
  const lurches: LurchEvent[] = [];
  if (gradeTier >= 2) {
    const n = 1 + Math.floor(rnd() * 3);
    for (let i = 0; i < n; i++) {
      lurches.push({
        t_ms: Math.floor((0.2 + rnd() * 0.6) * duration * 1000),
        gauge: Math.floor(rnd() * gauges),
        mag: (rnd() < 0.5 ? -1 : 1) * (0.08 + rnd() * 0.1),
      });
    }
    lurches.sort((a, b) => a.t_ms - b.t_ms);
  }
  const ghostNo = 100 + Math.floor(rnd() * 8900);
  return {
    system: 'FORM2V',
    seed,
    grade_tier: gradeTier,
    gauges,
    band_width: bandWidth,
    duration_s: duration,
    drift,
    lurches,
    deliberation: {
      designation: `PETITIONER #${String(ghostNo).padStart(4, '0')}-A`,
      genre: pickSeeded(GHOST_GENRES, seed + ':genre'),
    },
  };
}

/** Drift velocity for a gauge at time t (seconds). Pure. */
export function gaugeDriftVel(spec: Form2VSpec, gauge: number, tS: number): number {
  let v = 0;
  for (const c of spec.drift[gauge]) v += c.a * Math.sin(2 * Math.PI * c.f * tS + c.p);
  return v;
}

export interface GaugeInputs {
  bleeds: Array<{ g: number; t0: number; t1: number }>; // ms from t0
  feeds: Array<{ g: number; t: number }>;
}

export interface GaugeSimResult {
  excursion_s: number[]; // per gauge, seconds out of band
  total_excursion_s: number;
  worst_single_s: number;
  actions: number;
  final_positions: number[];
}

/**
 * Full deterministic needle replay — the bench's purest anti-cheat case.
 * Client renders with the same function; server trusts only this.
 */
export function simulateGauges(spec: Form2VSpec, inputs: GaugeInputs): GaugeSimResult {
  const durMs = spec.duration_s * 1000;
  const half = spec.band_width / 2;
  const x: number[] = new Array(spec.gauges).fill(0.5);
  const exc: number[] = new Array(spec.gauges).fill(0);
  const worstRun: number[] = new Array(spec.gauges).fill(0);
  const curRun: number[] = new Array(spec.gauges).fill(0);
  let li = 0;
  const feeds = [...inputs.feeds].sort((a, b) => a.t - b.t);
  let fi = 0;
  const dt = GAUGE_SIM_DT_MS;

  for (let t = 0; t < durMs; t += dt) {
    // lurches (instant steps)
    while (li < spec.lurches.length && spec.lurches[li].t_ms <= t) {
      const L = spec.lurches[li];
      if (L.gauge < spec.gauges) x[L.gauge] = clamp01(x[L.gauge] + L.mag);
      li++;
    }
    // feed taps
    while (fi < feeds.length && feeds[fi].t <= t) {
      const F = feeds[fi];
      if (F.g >= 0 && F.g < spec.gauges) x[F.g] = clamp01(x[F.g] + GAUGE_FEED_KICK);
      fi++;
    }
    for (let g = 0; g < spec.gauges; g++) {
      let v = gaugeDriftVel(spec, g, t / 1000);
      for (const b of inputs.bleeds) {
        if (b.g === g && t >= b.t0 && t < b.t1) v -= GAUGE_BLEED_RATE;
      }
      x[g] = clamp01(x[g] + (v * dt) / 1000);
      if (Math.abs(x[g] - 0.5) > half) {
        exc[g] += dt / 1000;
        curRun[g] += dt / 1000;
        if (curRun[g] > worstRun[g]) worstRun[g] = curRun[g];
      } else {
        curRun[g] = 0;
      }
    }
  }
  const total = exc.reduce((a, b) => a + b, 0);
  return {
    excursion_s: exc.map((v) => Math.round(v * 10) / 10),
    total_excursion_s: Math.round(total * 10) / 10,
    worst_single_s: Math.round(Math.max(...worstRun, 0) * 10) / 10,
    actions: inputs.bleeds.length + inputs.feeds.length,
    final_positions: x.map((v) => Math.round(v * 1000) / 1000),
  };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// ---------------------------------------------------------------------------
// FORM 33-C — THE ROUTE OF CONTRITION
// The waypoint graph is AUTHORITATIVE here (1 unit = 1m, hall origin at the
// Intake Hall southwest corner). The client's world layout aligns to these
// coordinates; the server verifies routes against the same polyline.
// ---------------------------------------------------------------------------

export interface Waypoint {
  id: string;
  x: number;
  y: number;
  adj: string[];
}

// Authored against the REAL floor plan in src/client/world/layout.ts.
// Mapping (client contrition.ts): world_x = 31 - wp.x, world_z = 17 - wp.y.
// Every adjacency below has wall-free, prop-free line-of-sight through a
// legal door gap — enforced by scripts/check-waypoints.mjs (run it in CI).
// Door gaps used: turnstile x=14 z[-6,-3]; intake|gallery x=14 z[7,10];
// hall|gallery z=6 x[-8,-5] and x[6,9]; hall|rotunda x=-12 z[-2,2];
// hall|archive x=-12 z[-13,-10]; archive|rotunda z=-6 x[-22,-19];
// rotunda|registry z=6 x[-27,-25].
export const WAYPOINTS: Waypoint[] = [
  // Intake Hall (world x 14..30)
  { id: 'intake_n', x: 9, y: 26, adj: ['intake_c', 'turnstile'] }, // world (22,-9)
  { id: 'intake_c', x: 9, y: 14, adj: ['intake_n', 'gallery_arch'] }, // world (22,3)
  // Door-gap connectors
  { id: 'turnstile', x: 17, y: 21.5, adj: ['intake_n', 'row_e'] }, // world (14,-4.5)
  { id: 'gallery_arch', x: 17, y: 8.5, adj: ['intake_c', 'gallery_e'] }, // world (14,8.5)
  // Window Row (z=-10 lane, aligned with the desk-column corridors)
  { id: 'row_e', x: 26.5, y: 27, adj: ['turnstile', 'row_c', 'desk_e'] }, // world (4.5,-10)
  { id: 'row_c', x: 31.5, y: 27, adj: ['row_e', 'row_w'] }, // world (-0.5,-10)
  { id: 'row_w', x: 36.5, y: 27, adj: ['row_c', 'desk_w', 'archive_e'] }, // world (-5.5,-10)
  // Writing Floor (z=0 lane between the desk rows)
  { id: 'desk_e', x: 26.5, y: 17, adj: ['row_e', 'desk_w', 'gallery_e'] }, // world (4.5,0)
  { id: 'desk_w', x: 36.5, y: 17, adj: ['row_w', 'desk_e', 'rotunda_c', 'gallery_w'] }, // world (-5.5,0)
  // Public Record Gallery
  { id: 'gallery_e', x: 23, y: 8, adj: ['gallery_arch', 'gallery_w', 'desk_e'] }, // world (8,9)
  { id: 'gallery_w', x: 37.5, y: 8, adj: ['gallery_e', 'desk_w'] }, // world (-6.5,9)
  // The Rotunda
  { id: 'rotunda_c', x: 47, y: 17, adj: ['desk_w', 'rotunda_s'] }, // world (-16,0)
  { id: 'rotunda_s', x: 52, y: 13, adj: ['rotunda_c', 'vault_s', 'vault_face'] }, // world (-21,4)
  { id: 'vault_s', x: 57, y: 13, adj: ['rotunda_s', 'vault_face', 'purge_e', 'arch_gate'] }, // world (-26,4)
  { id: 'vault_face', x: 58.5, y: 14.2, adj: ['vault_s', 'rotunda_s'] }, // world (-27.5,2.8)
  // The Archive (arch_gate threads the z=-6 door gap WEST of the podium cluster)
  { id: 'arch_gate', x: 52.5, y: 21.5, adj: ['arch_s', 'vault_s'] }, // world (-21.5,-4.5)
  { id: 'arch_s', x: 51.5, y: 25.5, adj: ['arch_gate', 'arch_c'] }, // world (-20.5,-8.5)
  { id: 'arch_c', x: 51.5, y: 28, adj: ['arch_s', 'archive_e'] }, // world (-20.5,-11)
  { id: 'archive_e', x: 46, y: 28.5, adj: ['arch_c', 'row_w'] }, // world (-15,-11.5)
  // Registry of the Purged (a dead end, diegetically)
  { id: 'purge_e', x: 57, y: 9, adj: ['vault_s', 'purge_w'] }, // world (-26,8)
  { id: 'purge_w', x: 58, y: 4.5, adj: ['purge_e'] }, // world (-27,12.5)
];

const STATION_FLAVORS = ['the_vault', 'the_registry', 'your_drawer', 'window_4', 'the_hall_clock', 'the_plaque'];

export interface Form33CStation {
  id: number;
  wp: string; // waypoint id
  dwell_ms: number; // 3000-8000
  bearing_deg: number; // required facing during dwell (+/- 25)
  flavor_key: string;
}

export interface Form33CSpec {
  system: 'FORM33C';
  seed: string;
  grade_tier: number;
  circuit: string[]; // waypoint ids, closed loop (last connects to first)
  stations: Form33CStation[];
  pace_min: number; // 0.7
  pace_max: number; // 1.1
  tolerance_m: number; // corridor half-width
  penance: boolean;
}

function wpById(id: string): Waypoint {
  const wp = WAYPOINTS.find((w) => w.id === id);
  if (!wp) throw new Error(`unknown waypoint ${id}`);
  return wp;
}

/** Deterministic BFS path from -> to over the adjacency graph (inclusive). */
export function graphPath(from: string, to: string): string[] {
  if (from === to) return [from];
  const prev = new Map<string, string>();
  const queue = [from];
  prev.set(from, from);
  while (queue.length) {
    const cur = queue.shift()!;
    for (const n of wpById(cur).adj) {
      if (prev.has(n)) continue;
      prev.set(n, cur);
      if (n === to) {
        const path = [to];
        let p = to;
        while (p !== from) {
          p = prev.get(p)!;
          path.push(p);
        }
        return path.reverse();
      }
      queue.push(n);
    }
  }
  return [from, to]; // unreachable on a connected graph; degrade to the chord
}

export function circuitPolyline(circuit: string[]): Array<[number, number]> {
  const pts = circuit.map((id) => {
    const w = wpById(id);
    return [w.x, w.y] as [number, number];
  });
  pts.push(pts[0]); // closed
  return pts;
}

export function polylineLength(pts: Array<[number, number]>): number {
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  }
  return Math.round(len * 10) / 10;
}

/** Distance from point to a polyline (meters). Pure geometry, both sides. */
export function distToPolyline(pts: Array<[number, number]>, x: number, y: number): number {
  let best = Infinity;
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1];
    const [bx, by] = pts[i];
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / lenSq));
    const px = ax + t * dx;
    const py = ay + t * dy;
    const d = Math.hypot(x - px, y - py);
    if (d < best) best = d;
  }
  return best;
}

export function gen33C(seed: string, gradeTier: number, penance: boolean): Form33CSpec {
  const rnd = streamFrom(seed + ':33c');
  // seeded walk over the graph until ~70m+, then close the loop
  const startIdx = Math.floor(rnd() * WAYPOINTS.length);
  let current = WAYPOINTS[startIdx];
  const circuit: string[] = [current.id];
  let guard = 0;
  while (guard++ < 40) {
    const options = current.adj.filter((a) => !circuit.includes(a));
    const next = options.length
      ? wpById(options[Math.floor(rnd() * options.length)])
      : wpById(current.adj[Math.floor(rnd() * current.adj.length)]);
    if (circuit.includes(next.id)) break;
    circuit.push(next.id);
    current = next;
    if (circuit.length >= 5 && polylineLength(circuitPolyline(circuit)) >= 60) break;
  }
  while (circuit.length < 4) {
    const tail = wpById(circuit[circuit.length - 1]);
    const nxt = tail.adj.find((a) => !circuit.includes(a));
    if (nxt) {
      circuit.push(nxt);
      continue;
    }
    // tail is a dead end (the Registry) — grow from the head instead
    const head = wpById(circuit[0]);
    const prev = head.adj.find((a) => !circuit.includes(a));
    if (!prev) break;
    circuit.unshift(prev);
  }
  // Close the loop ALONG GRAPH EDGES: circuitPolyline appends the origin, so
  // the closing segment must be an adjacency, never a wall-crossing chord.
  const closure = graphPath(circuit[circuit.length - 1], circuit[0]);
  for (const id of closure.slice(1, -1)) circuit.push(id);
  const stationCount = Math.min(circuit.length, 4 + Math.floor(rnd() * 3)); // 4-6
  const step = circuit.length / stationCount;
  const stations: Form33CStation[] = [];
  for (let i = 0; i < stationCount; i++) {
    const wp = circuit[Math.min(circuit.length - 1, Math.floor(i * step))];
    stations.push({
      id: i + 1,
      wp,
      dwell_ms: 3000 + Math.floor(rnd() * 5001),
      bearing_deg: Math.floor(rnd() * 360),
      flavor_key: STATION_FLAVORS[Math.floor(rnd() * STATION_FLAVORS.length)],
    });
  }
  return {
    system: 'FORM33C',
    seed,
    grade_tier: gradeTier,
    circuit,
    stations,
    pace_min: 0.7,
    pace_max: 1.1,
    tolerance_m: gradeTier <= 1 ? 1.0 : 0.6,
    penance,
  };
}

// ---------------------------------------------------------------------------
// FORM 0-0 — EXERCISE ZERO
// ---------------------------------------------------------------------------

export type Form00Variant = 'plain' | 'already_down' | 'twin_lever';

export interface Form00Spec {
  system: 'FORM00';
  seed: string;
  grade_tier: number;
  variant: Form00Variant;
  consequence: number; // index into the lever consequence pool
  window_s: number;
}

export const FORM00_CONSEQUENCES = [
  'nothing',
  'one flicker',
  'one distant arm slam',
  'your designation printed once, unprompted',
];

export function gen00(seed: string, gradeTier: number): Form00Spec {
  let variant: Form00Variant = 'plain';
  if (fnv1a(seed + ':twin') % 12 === 0) variant = 'twin_lever';
  else if (gradeTier >= 3 && fnv1a(seed + ':down') % 10 < 3) variant = 'already_down';
  return {
    system: 'FORM00',
    seed,
    grade_tier: gradeTier,
    variant,
    consequence: fnv1a(seed + ':conseq') % FORM00_CONSEQUENCES.length,
    window_s: 90,
  };
}

// ---------------------------------------------------------------------------
// FORM 8-R — JUNCTION RELIEF DUTY (switchboard routing rush)
// The full canister schedule is a pure function of the spec; the server
// replays correctness from {n, lever, t} alone. Glyph/correctness are NEVER
// client-asserted.
// ---------------------------------------------------------------------------

export const GLYPHS_8R = [
  'ARCHIVE',
  'APPEALS',
  'RECORDS',
  'ROTUNDA',
  'INTAKE',
  'SANCTUM',
  'SHREDDING',
  'DEPT6', // green phosphor; routing one here is the only v1 way anything goes THERE
] as const;

export interface Form8RSpec {
  system: 'FORM8R';
  seed: string;
  grade_tier: number;
  lever_count: number; // 4-6
  lever_glyphs: string[]; // active glyphs, index = lever number
  count: number; // 14-24 canisters
  start_interval_ms: number; // 6000
  floor_interval_ms: number; // 4500 -> 2200 by grade
  forced_haste: boolean; // HASTE AUTHORIZED. DIGNITY WAIVED. (one misroute forgiven)
  out_of_service: number | null; // grade 5: lever index OUT, traffic remapped
  remap_to: number | null;
  dest: number[]; // per-canister glyph slot (lever index before remap)
  dual: Array<number | null>; // grade 4: alternate acceptable lever per canister
  kindness_idxs: number[]; // canisters bearing a witnessed designation (memo-only)
}

export function gen8R(seed: string, gradeTier: number): Form8RSpec {
  const rnd = streamFrom(seed + ':8r');
  const g = Math.max(1, Math.min(5, gradeTier));
  const leverCount = g <= 1 ? 4 : g <= 3 ? 5 : 6;
  const count = [14, 14, 16, 18, 20, 24][g] || 16;
  const floor = [4500, 4500, 3800, 3200, 2800, 2200][g] || 3800;
  const forcedHaste = g >= 3 && rnd() < 0.18;
  // active glyph pool: DEPT6 kept in rotation from grade 2
  const pool = [...GLYPHS_8R.slice(0, 7)];
  const glyphs: string[] = [];
  while (glyphs.length < leverCount) {
    const pick = pool.splice(Math.floor(rnd() * pool.length), 1)[0];
    glyphs.push(pick);
  }
  if (g >= 2 && !glyphs.includes('DEPT6') && rnd() < 0.35) glyphs[leverCount - 1] = 'DEPT6';
  const outOfService = g >= 5 ? Math.floor(rnd() * leverCount) : null;
  const remapTo =
    outOfService === null ? null : (outOfService + 1 + Math.floor(rnd() * (leverCount - 1))) % leverCount;
  const dest: number[] = [];
  for (let i = 0; i < count; i++) {
    let d = Math.floor(rnd() * leverCount);
    // no glyph three times in a row
    if (i >= 2 && dest[i - 1] === d && dest[i - 2] === d) d = (d + 1) % leverCount;
    dest.push(d);
  }
  const dual: Array<number | null> = dest.map((d) =>
    g >= 4 && rnd() < 0.25 ? (d + 1 + Math.floor(rnd() * (leverCount - 1))) % leverCount : null
  );
  const kindness: number[] = [];
  for (let i = 0; i < count; i++) if (rnd() < 0.08) kindness.push(i);
  return {
    system: 'FORM8R',
    seed,
    grade_tier: gradeTier,
    lever_count: leverCount,
    lever_glyphs: glyphs,
    count,
    start_interval_ms: 6000,
    floor_interval_ms: forcedHaste ? Math.round(floor * 0.85) : floor,
    forced_haste: forcedHaste,
    out_of_service: outOfService,
    remap_to: remapTo,
    dest,
    dual,
    kindness_idxs: kindness,
  };
}

/** Arrival time (ms from t0) per canister — interval decays linearly to floor. */
export function arrivals8R(spec: Form8RSpec): number[] {
  const out: number[] = [];
  let t = 2500;
  for (let i = 0; i < spec.count; i++) {
    out.push(Math.round(t));
    const frac = spec.count <= 1 ? 1 : i / (spec.count - 1);
    t += spec.start_interval_ms - (spec.start_interval_ms - spec.floor_interval_ms) * frac;
  }
  return out;
}

/** Decision window for canister n (arrival -> gate). */
export function window8R(spec: Form8RSpec, n: number): number {
  const frac = spec.count <= 1 ? 1 : n / (spec.count - 1);
  const interval = spec.start_interval_ms - (spec.start_interval_ms - spec.floor_interval_ms) * frac;
  return Math.max(1800, Math.round(interval * 1.4));
}

/** Correct lever(s) for canister n, out-of-service remap applied. */
export function expectedLevers8R(spec: Form8RSpec, n: number): number[] {
  const apply = (d: number): number => (spec.out_of_service === d && spec.remap_to !== null ? spec.remap_to : d);
  const out = [apply(spec.dest[n])];
  const alt = spec.dual[n];
  if (alt !== null && alt !== undefined) out.push(apply(alt));
  return out;
}

// ---------------------------------------------------------------------------
// FORM 5-D — THE DISCREPANCY WINDOW (document inspection under changing rules)
// Docs + planted violations regenerate from the seed on both sides.
// ---------------------------------------------------------------------------

export type Field5D = 'seal' | 'date' | 'designation' | 'fee' | 'signature' | 'watermark';

export interface Rule5D {
  id: number;
  field: Field5D;
  bad: number; // the value that violates this rule
  text: string;
}

// 24-rule pool, all machine-checkable (field === bad value -> violation).
export const RULES_5D: Rule5D[] = (() => {
  const rules: Rule5D[] = [];
  const sealNames = ['THE BROKEN RING', 'THE INVERTED ARCH', 'THE DOUBLED EYE', 'THE HOLLOW STAR', 'THE LEANING GATE', 'THE SECOND MOON'];
  for (let k = 0; k < 6; k++) rules.push({ id: rules.length, field: 'seal', bad: k, text: `DOCUMENTS BEARING SEAL ${sealNames[k]} ARE INVALID.` });
  for (let k = 0; k < 4; k++) rules.push({ id: rules.length, field: 'date', bad: k, text: `DATES OF THE ${['FIRST', 'SECOND', 'THIRD', 'FOURTH'][k]} QUARTER ARE NOT ACCEPTED THIS SHIFT.` });
  for (let k = 0; k < 4; k++) rules.push({ id: rules.length, field: 'designation', bad: k, text: `DESIGNATION SERIES ${['-A', '-B', '-C', '-D'][k]} REQUIRES COUNTERSIGNATURE. NONE PRESENT: INVALID.` });
  for (let k = 0; k < 4; k++) rules.push({ id: rules.length, field: 'fee', bad: k, text: `FEE STAMPS OF DENOMINATION ${k + 1} WERE WITHDRAWN. WITHDRAWN STAMPS DO NOT PAY.` });
  for (let k = 0; k < 3; k++) rules.push({ id: rules.length, field: 'signature', bad: k, text: `SIGNATURES IN INK HUE ${['GREY', 'VIOLET', 'RUST'][k]} ARE NOT SIGNATURES.` });
  for (let k = 0; k < 3; k++) rules.push({ id: rules.length, field: 'watermark', bad: k, text: `THE ${['CHEVRON', 'LATTICE', 'RING'][k]} WATERMARK BELONGS TO A DISSOLVED DEPARTMENT.` });
  return rules;
})();

export const FIELD_DOMAIN_5D: Record<Field5D, number> = {
  seal: 12,
  date: 8,
  designation: 8,
  fee: 8,
  signature: 6,
  watermark: 6,
};

export interface Doc5D {
  fields: Record<Field5D, number>;
  violations: Array<{ rule_id: number; field: Field5D }>;
  decoy: boolean;
}

export interface Form5DSpec {
  system: 'FORM5D';
  seed: string;
  grade_tier: number;
  rule_ids: number[]; // 3-6 of the 24-pool
  doc_count: number; // 8-14
  amendment_at: number | null; // doc index from which the amendment applies
  amendment_rule_id: number | null;
  decoy_idx: number; // the engineered legitimate-but-suspicious near-miss
}

export function gen5D(seed: string, gradeTier: number): Form5DSpec {
  const rnd = streamFrom(seed + ':5d');
  const g = Math.max(0, Math.min(5, gradeTier));
  const ruleCount = g <= 1 ? 3 : g === 2 ? 4 : 5 + (rnd() < 0.5 ? 1 : 0);
  const docCount = g <= 1 ? 8 : g === 2 ? 10 : 14;
  const ids = new Set<number>();
  while (ids.size < ruleCount) ids.add(Math.floor(rnd() * RULES_5D.length));
  const ruleIds = [...ids];
  let amendmentRule: number | null = null;
  let amendmentAt: number | null = null;
  if (g >= 2) {
    let r = Math.floor(rnd() * RULES_5D.length);
    while (ruleIds.includes(r)) r = (r + 1) % RULES_5D.length;
    amendmentRule = r;
    amendmentAt = Math.floor(docCount * (0.4 + rnd() * 0.2));
  }
  return {
    system: 'FORM5D',
    seed,
    grade_tier: gradeTier,
    rule_ids: ruleIds,
    doc_count: docCount,
    amendment_at: amendmentAt,
    amendment_rule_id: amendmentRule,
    decoy_idx: Math.floor(rnd() * docCount),
  };
}

/** Rules applicable to doc i (the amendment reprints mid-shift). */
export function rulesForDoc5D(spec: Form5DSpec, i: number): number[] {
  const ids = [...spec.rule_ids];
  if (spec.amendment_rule_id !== null && spec.amendment_at !== null && i >= spec.amendment_at) {
    ids.push(spec.amendment_rule_id);
  }
  return ids;
}

/** Pure doc-set regeneration — identical on client and worker. */
export function docs5D(spec: Form5DSpec): Doc5D[] {
  const rnd = streamFrom(spec.seed + ':5ddocs');
  const docs: Doc5D[] = [];
  let forgeries = 0;
  for (let i = 0; i < spec.doc_count; i++) {
    const applicable = rulesForDoc5D(spec, i).map((id) => RULES_5D[id]);
    // clean values first: avoid every applicable rule's bad value
    const fields = {} as Record<Field5D, number>;
    (Object.keys(FIELD_DOMAIN_5D) as Field5D[]).forEach((f) => {
      const badSet = new Set(applicable.filter((r) => r.field === f).map((r) => r.bad));
      let v = Math.floor(rnd() * FIELD_DOMAIN_5D[f]);
      let guard = 0;
      while (badSet.has(v) && guard++ < 16) v = Math.floor(rnd() * FIELD_DOMAIN_5D[f]);
      fields[f] = v;
    });
    const decoy = i === spec.decoy_idx;
    const violations: Array<{ rule_id: number; field: Field5D }> = [];
    if (!decoy) {
      const roll = rnd();
      const nViol = roll < 0.5 ? 0 : roll < 0.9 ? 1 : 2;
      const shuffledRules = [...applicable].sort((a, b) => fnv1a(spec.seed + i + a.id) - fnv1a(spec.seed + i + b.id));
      for (const r of shuffledRules.slice(0, nViol)) {
        if (violations.some((v) => v.field === r.field)) continue;
        fields[r.field] = r.bad;
        violations.push({ rule_id: r.id, field: r.field });
      }
    }
    if (violations.length) forgeries++;
    docs.push({ fields, violations, decoy });
  }
  // guarantee at least one forgery per shift (the window exists for a reason)
  if (forgeries === 0) {
    const i = (spec.decoy_idx + 1) % spec.doc_count;
    const r = RULES_5D[spec.rule_ids[0]];
    docs[i].fields[r.field] = r.bad;
    docs[i].violations.push({ rule_id: r.id, field: r.field });
  }
  return docs;
}

// ---------------------------------------------------------------------------
// FORM 5-M — MANUAL CONVEYANCE DUTY (fragile / leaking / forbidden / waste)
// Cradles sit AT authored waypoints so every route is walkable by
// construction; jolts/drips/lamp observations are recomputed server-side
// from the 100ms carry-phase route samples.
// ---------------------------------------------------------------------------

export type CargoCondition = 'FRAGILE' | 'LEAKING' | 'FORBIDDEN' | 'WASTE';

export const CRADLES_5M: Array<{ id: string; wp: string }> = [
  { id: 'CRADLE-W2', wp: 'row_e' },
  { id: 'CRADLE-W4', wp: 'row_c' },
  { id: 'CRADLE-W6', wp: 'row_w' },
  { id: 'CRADLE-DESK-E', wp: 'desk_e' },
  { id: 'CRADLE-DESK-W', wp: 'desk_w' },
  { id: 'CRADLE-GALLERY', wp: 'gallery_e' },
  { id: 'CRADLE-GALLERY-W', wp: 'gallery_w' },
  { id: 'CRADLE-INTAKE', wp: 'intake_c' },
  { id: 'CRADLE-INTAKE-N', wp: 'intake_n' },
  { id: 'CRADLE-ROTUNDA', wp: 'rotunda_c' },
  { id: 'CRADLE-VAULT', wp: 'rotunda_s' },
  { id: 'CRADLE-ARCHIVE', wp: 'arch_c' },
  { id: 'CRADLE-ARCHIVE-E', wp: 'archive_e' },
  { id: 'CRADLE-PURGE', wp: 'purge_e' },
];

/** Carpet rectangles per variant, waypoint space [x0,y0,x1,y1]. */
export const CARPETS_5M: Array<Array<[number, number, number, number]>> = [
  [
    [20, 6, 40, 10.5], // the Gallery runner
    [6, 12, 12, 28], // Intake Hall carpet
  ],
  [
    [24, 15, 39, 19], // Writing Floor lane carpet
    [45, 11, 55, 16], // Rotunda approach
  ],
];

/** Seeded-sweep eye-lamps (FORBIDDEN cargo), waypoint space. */
export const LAMPS_5M: Array<{ x: number; y: number; r: number; period_ms: number; duty: number }> = [
  { x: 26.5, y: 27, r: 3.2, period_ms: 7000, duty: 0.35 }, // Window Row east
  { x: 36.5, y: 27, r: 3.2, period_ms: 9000, duty: 0.35 }, // Window Row west
  { x: 31.5, y: 17, r: 3.5, period_ms: 8000, duty: 0.4 }, // Writing Floor center
  { x: 47, y: 17, r: 3.5, period_ms: 6000, duty: 0.35 }, // Rotunda mouth
  { x: 23, y: 8, r: 3.0, period_ms: 10000, duty: 0.3 }, // Gallery arch
];

export const TAG_FLAVORS_5M = [
  'CONTENTS: ONE (1) SIGH, NOTARIZED',
  'CONTENTS: SEVENTEEN (17) UNCLAIMED THANKS',
  'CONTENTS: A DOUBT, SEALED FOR TRANSIT',
  'CONTENTS: FORMS ABOUT FORMS. DO NOT FEED',
  'CONTENTS: THE MORNING SHIFT. REMAINDER OF',
  'CONTENTS: ONE (1) APOLOGY, PRE-OWNED',
  'CONTENTS: REGRET, CONCENTRATED. DILUTE BEFORE FILING',
  'CONTENTS: MISCELLANEOUS OBEDIENCE',
];

export interface Form5MSpec {
  system: 'FORM5M';
  seed: string;
  grade_tier: number;
  conditions: CargoCondition[]; // 1 (grade 1-2) or 2 stacked (grade 3+)
  origin: string; // cradle id
  dest: string; // cradle id
  deadline_s: number; // 45-120, from task t0
  lamp_phase_ms: number;
  carpet_variant: number;
  tag_flavor: string;
  observe_cap_ms: number; // FORBIDDEN observation budget
  route_len_m: number; // nominal graph path length (client hint, server-derived)
}

export function cradleCoord(cradleId: string): [number, number] {
  const c = CRADLES_5M.find((c) => c.id === cradleId);
  if (!c) throw new Error(`unknown cradle ${cradleId}`);
  const wp = WAYPOINTS.find((w) => w.id === c.wp)!;
  return [wp.x, wp.y];
}

export function gen5M(seed: string, gradeTier: number): Form5MSpec {
  const rnd = streamFrom(seed + ':5m');
  const g = Math.max(1, Math.min(5, gradeTier));
  const singles: CargoCondition[] = ['FRAGILE', 'LEAKING', 'FRAGILE', 'LEAKING', 'WASTE'];
  let conditions: CargoCondition[];
  if (g >= 5) conditions = ['FORBIDDEN'];
  else if (g >= 3) {
    const first = singles[Math.floor(rnd() * singles.length)];
    const second: CargoCondition = first === 'FRAGILE' ? 'LEAKING' : 'FRAGILE';
    conditions = rnd() < 0.6 ? [first, second] : [first];
    if (g >= 4 && rnd() < 0.3) conditions = ['FORBIDDEN'];
  } else {
    conditions = [singles[Math.floor(rnd() * 4)]];
  }
  // origin from the Window Row cradles, destination elsewhere, path >= 14m
  const originPool = CRADLES_5M.slice(0, 5);
  const origin = originPool[Math.floor(rnd() * originPool.length)];
  let dest = CRADLES_5M[Math.floor(rnd() * CRADLES_5M.length)];
  let guard = 0;
  let pathLen = 0;
  while (guard++ < 24) {
    if (dest.id !== origin.id) {
      const path = graphPath(origin.wp, dest.wp);
      pathLen = polylineLength(path.map((id) => {
        const w = WAYPOINTS.find((x) => x.id === id)!;
        return [w.x, w.y] as [number, number];
      }));
      if (pathLen >= 14) break;
    }
    dest = CRADLES_5M[Math.floor(rnd() * CRADLES_5M.length)];
  }
  const slack = g <= 1 ? 40 : g === 2 ? 28 : 22;
  const deadline = Math.max(45, Math.min(120, Math.round(pathLen / 0.9 + slack)));
  return {
    system: 'FORM5M',
    seed,
    grade_tier: gradeTier,
    conditions,
    origin: origin.id,
    dest: dest.id,
    deadline_s: deadline,
    lamp_phase_ms: Math.floor(rnd() * 10000),
    carpet_variant: Math.floor(rnd() * CARPETS_5M.length),
    tag_flavor: TAG_FLAVORS_5M[Math.floor(rnd() * TAG_FLAVORS_5M.length)],
    observe_cap_ms: g >= 5 ? 2500 : 4000,
    route_len_m: Math.round(pathLen * 10) / 10,
  };
}

export function inCarpet5M(spec: Form5MSpec, x: number, y: number): boolean {
  for (const [x0, y0, x1, y1] of CARPETS_5M[spec.carpet_variant] || []) {
    if (x >= x0 && x <= x1 && y >= y0 && y <= y1) return true;
  }
  return false;
}

/** Lamp on-phase test at time t (ms from t0). */
export function lampOn5M(spec: Form5MSpec, lampIdx: number, t: number): boolean {
  const L = LAMPS_5M[lampIdx];
  return (t + spec.lamp_phase_ms) % L.period_ms < L.duty * L.period_ms;
}

// ---------------------------------------------------------------------------
// FORM 5-S — RETENTION DUTY (what shreds vs what files)
// The directive, the docs, and the after-action annex all regenerate from
// the seed. Scoring is rule-compliance ONLY; the reveal is prose.
// ---------------------------------------------------------------------------

export interface Clause5S {
  id: number;
  field: 'watermark' | 'seal' | 'dept' | 'age' | 'duplicate';
  value: number; // matches when doc[field] === value (duplicate: 1)
  action: 'SHRED' | 'RETAIN';
  text: string;
}

export const CLAUSES_5S: Clause5S[] = [
  { id: 0, field: 'watermark', value: 0, action: 'SHRED', text: 'ALL DOCUMENTS BEARING THE CHEVRON WATERMARK: SHRED.' },
  { id: 1, field: 'watermark', value: 1, action: 'RETAIN', text: 'THE LATTICE WATERMARK IS UNDER AUDIT. LATTICE DOCUMENTS: RETAIN.' },
  { id: 2, field: 'seal', value: 0, action: 'RETAIN', text: 'DOCUMENTS UNDER THE REGISTRAR SEAL: RETAIN, REGARDLESS OF CONDITION.' },
  { id: 3, field: 'seal', value: 1, action: 'SHRED', text: 'THE PROVISIONAL SEAL EXPIRED ON THE 4TH. PROVISIONAL DOCUMENTS: SHRED.' },
  { id: 4, field: 'dept', value: 2, action: 'SHRED', text: 'DEPARTMENT 2 CORRESPONDENCE IS DUPLICATED ELSEWHERE. SHRED.' },
  { id: 5, field: 'dept', value: 4, action: 'RETAIN', text: 'DEPARTMENT 4 NO LONGER EXISTS. ITS PAPERS ARE THEREFORE IRREPLACEABLE. RETAIN.' },
  { id: 6, field: 'age', value: 2, action: 'SHRED', text: 'DOCUMENTS OF THE OLD CALENDAR (THIRD AGE): SHRED. THE OLD CALENDAR WAS WRONG.' },
  { id: 7, field: 'age', value: 0, action: 'RETAIN', text: 'CURRENT-CYCLE DOCUMENTS: RETAIN. THE CYCLE IS STILL OCCURRING.' },
  { id: 8, field: 'duplicate', value: 1, action: 'SHRED', text: 'WHERE A DUPLICATE EXISTS, THE COPY IS AN INSULT TO THE ORIGINAL. SHRED DUPLICATES.' },
  { id: 9, field: 'dept', value: 6, action: 'RETAIN', text: 'DEPARTMENT 6 MATERIAL: RETAIN. DO NOT READ. RETAIN.' },
];

export interface Doc5S {
  watermark: number; // 0-2
  seal: number; // 0-2
  dept: number; // 1-6
  age: number; // 0-2
  duplicate: number; // 0|1
  about_you: boolean; // one doc appears to be about the petitioner (flavor)
}

export interface Form5SSpec {
  system: 'FORM5S';
  seed: string;
  grade_tier: number;
  clause_ids: number[]; // 2-4, printed precedence = array order
  doc_count: number; // 8-12
  contradiction_idx: number | null;
  reveal_register: number; // 0-3: loss / junk / gratitude / silence
  waste_egress: boolean; // grade 5: seal + carry to the DISPOSAL HATCH
  hatch_period_ms: number;
  hatch_open_ms: number;
}

export function gen5S(seed: string, gradeTier: number): Form5SSpec {
  const rnd = streamFrom(seed + ':5s');
  const g = Math.max(1, Math.min(5, gradeTier));
  const clauseCount = g <= 1 ? 2 : g === 2 ? 3 : 4;
  const ids = new Set<number>();
  while (ids.size < clauseCount) ids.add(Math.floor(rnd() * CLAUSES_5S.length));
  const clauseIds = [...ids];
  const docCount = g <= 1 ? 8 : g === 2 ? 10 : 12;
  const contradiction = g >= 3 && rnd() < 0.5 ? Math.floor(rnd() * docCount) : null;
  return {
    system: 'FORM5S',
    seed,
    grade_tier: gradeTier,
    clause_ids: clauseIds,
    doc_count: docCount,
    contradiction_idx: contradiction,
    reveal_register: Math.floor(rnd() * 4),
    waste_egress: g >= 5,
    hatch_period_ms: 14000,
    hatch_open_ms: 3500,
  };
}

/** Truth for a doc under printed precedence (first matching clause rules). */
export function truth5S(spec: Form5SSpec, doc: Doc5S): 'SHRED' | 'RETAIN' {
  for (const id of spec.clause_ids) {
    const c = CLAUSES_5S[id];
    if ((doc as unknown as Record<string, number>)[c.field] === c.value) return c.action;
  }
  return 'RETAIN'; // the Bureau defaults to keeping its memory
}

/** Pure doc-set regeneration: every clause matters at least once; the
 *  contradiction doc (grade 3+) matches two clauses with opposing actions. */
export function docs5S(spec: Form5SSpec): Array<{ doc: Doc5S; truth: 'SHRED' | 'RETAIN' | 'EITHER' }> {
  const rnd = streamFrom(spec.seed + ':5sdocs');
  const out: Array<{ doc: Doc5S; truth: 'SHRED' | 'RETAIN' | 'EITHER' }> = [];
  const aboutYou = Math.floor(rnd() * spec.doc_count);
  for (let i = 0; i < spec.doc_count; i++) {
    const doc: Doc5S = {
      watermark: Math.floor(rnd() * 3),
      seal: Math.floor(rnd() * 3),
      dept: 1 + Math.floor(rnd() * 6),
      age: Math.floor(rnd() * 3),
      duplicate: rnd() < 0.25 ? 1 : 0,
      about_you: i === aboutYou,
    };
    // force one doc per clause (cycled) so every clause matters
    if (i < spec.clause_ids.length) {
      const c = CLAUSES_5S[spec.clause_ids[i]];
      (doc as unknown as Record<string, number>)[c.field] = c.value;
      // clear earlier-precedence matches so THIS clause decides
      for (const pid of spec.clause_ids.slice(0, i)) {
        const p = CLAUSES_5S[pid];
        if (p.field !== c.field && (doc as unknown as Record<string, number>)[p.field] === p.value) {
          (doc as unknown as Record<string, number>)[p.field] = (p.value + 1) % 3 === p.value ? p.value + 2 : (p.value + 1) % 7;
        }
      }
    }
    let truth: 'SHRED' | 'RETAIN' | 'EITHER' = truth5S(spec, doc);
    if (i === spec.contradiction_idx && spec.clause_ids.length >= 2) {
      // engineer a doc matching the first SHRED clause and the first RETAIN clause
      const shredC = spec.clause_ids.map((id) => CLAUSES_5S[id]).find((c) => c.action === 'SHRED');
      const retainC = spec.clause_ids.map((id) => CLAUSES_5S[id]).find((c) => c.action === 'RETAIN');
      if (shredC && retainC && shredC.field !== retainC.field) {
        (doc as unknown as Record<string, number>)[shredC.field] = shredC.value;
        (doc as unknown as Record<string, number>)[retainC.field] = retainC.value;
        truth = 'EITHER';
      }
    }
    out.push({ doc, truth });
  }
  return out;
}

/** After-action annex lines per reveal register — server-templated, provable. */
export const REVEAL_5S: string[][] = [
  [
    'Among the shredded: the only surviving copy of a gratitude, filed before you were processed.',
    'The annex records the loss as TOTAL and the procedure as CORRECT. Both entries are accurate.',
  ],
  [
    'Among the shredded: forms about forms, and one memo requesting more memos.',
    'The annex records no loss. The Bureau is almost disappointed.',
  ],
  [
    'Among the shredded: a document that, in its final line, thanked whoever fed it to the teeth.',
    'The annex has filed the thanks. The thanks were addressed to you.',
  ],
  [
    'The annex declines to say what was in the bin.',
    'This is not a kindness. It is a policy. They are sometimes indistinguishable.',
  ],
];

// ---------------------------------------------------------------------------
// FORM 4-T — TERMINAL SUPPORT ROUNDS (percussive maintenance)
// Knob stability and slam force recompute from hold durations vs seed;
// kill-order correctness recomputes from card attributes vs the printed rule.
// ---------------------------------------------------------------------------

export type FixKind4T = 'cable' | 'knob' | 'slam' | 'kill';

export interface KillCard4T {
  id: number;
  impudence: number;
  age: number;
  size: number;
}

export interface Form4TSpec {
  system: 'FORM4T';
  seed: string;
  grade_tier: number;
  unit_id: number; // 11, 12, 13
  fixes: Array<{ kind: FixKind4T; params: Record<string, number> }>;
  order_enforced: boolean; // grade 2+: ticket order binds
  kill_rule: { key: 'impudence' | 'age' | 'size'; dir: 'desc' | 'asc' } | null;
  kill_cards: KillCard4T[];
  uncloseable: boolean; // RESOLVED (RECURRING)
}

export const KILL_RULES_4T: Array<{ key: 'impudence' | 'age' | 'size'; dir: 'desc' | 'asc'; text: string }> = [
  { key: 'impudence', dir: 'desc', text: 'TERMINATE IN ORDER OF IMPUDENCE, DESCENDING.' },
  { key: 'impudence', dir: 'asc', text: 'TERMINATE THE MEEK FIRST. IMPUDENCE, ASCENDING.' },
  { key: 'age', dir: 'desc', text: 'TERMINATE IN ORDER OF AGE, ELDEST FIRST. RESPECT COSTS NOTHING.' },
  { key: 'age', dir: 'asc', text: 'TERMINATE THE YOUNG JOBS FIRST. THEY HAVE LESS TO LOSE.' },
  { key: 'size', dir: 'desc', text: 'TERMINATE IN ORDER OF SIZE, DESCENDING. THE LARGE ONES KNOW WHY.' },
  { key: 'size', dir: 'asc', text: 'TERMINATE IN ORDER OF SIZE, ASCENDING. START SMALL. FINISH.' },
];

export function gen4T(seed: string, gradeTier: number): Form4TSpec {
  const rnd = streamFrom(seed + ':4t');
  const g = Math.max(1, Math.min(5, gradeTier));
  const fixCount = g <= 1 ? 2 : g === 2 ? 3 : 3 + (rnd() < 0.5 ? 1 : 0);
  const kinds: FixKind4T[] = ['cable', 'knob', 'slam', 'kill'];
  // seeded draw without replacement
  const drawn: FixKind4T[] = [];
  const pool = [...kinds];
  while (drawn.length < Math.min(fixCount, 4)) drawn.push(pool.splice(Math.floor(rnd() * pool.length), 1)[0]);
  const tolerance = g <= 1 ? 800 : g === 2 ? 650 : g === 3 ? 500 : 380;
  const slamWidth = g <= 1 ? 600 : g <= 3 ? 450 : 280; // NOT VINDICTIVE narrows
  const slamMin = 350 + Math.floor(rnd() * 150);
  const fixes = drawn.map((kind): { kind: FixKind4T; params: Record<string, number> } => {
    if (kind === 'knob') {
      return {
        kind,
        params: {
          target_ms: 1400 + Math.floor(rnd() * 2600),
          tolerance_ms: tolerance,
          false_plateau: g >= 4 && rnd() < 0.6 ? 1 : 0,
        },
      };
    }
    if (kind === 'slam') return { kind, params: { min_ms: slamMin, max_ms: slamMin + slamWidth } };
    if (kind === 'cable') return { kind, params: { socket_from: rnd() < 0.5 ? 0 : 1 } };
    return { kind, params: {} };
  });
  let killRule: Form4TSpec['kill_rule'] = null;
  let cards: KillCard4T[] = [];
  if (drawn.includes('kill')) {
    const kr = KILL_RULES_4T[Math.floor(rnd() * KILL_RULES_4T.length)];
    killRule = { key: kr.key, dir: kr.dir };
    const n = g >= 3 ? 5 : 4;
    const used = new Set<string>();
    for (let i = 0; i < n; i++) {
      let c: KillCard4T;
      do {
        c = {
          id: i,
          impudence: 1 + Math.floor(rnd() * 9),
          age: 1 + Math.floor(rnd() * 9),
          size: 1 + Math.floor(rnd() * 9),
        };
      } while (used.has(`${c.impudence}:${c.age}:${c.size}`) || cards.some((x) => x[killRule!.key] === c[killRule!.key]));
      used.add(`${c.impudence}:${c.age}:${c.size}`);
      cards.push(c);
    }
  }
  return {
    system: 'FORM4T',
    seed,
    grade_tier: gradeTier,
    unit_id: 11 + Math.floor(rnd() * 3),
    fixes,
    order_enforced: g >= 2,
    kill_rule: killRule,
    kill_cards: cards,
    uncloseable: rnd() < 0.08,
  };
}

/** Expected kill order (card ids) from the printed rule. */
export function killOrder4T(spec: Form4TSpec): number[] {
  if (!spec.kill_rule) return [];
  const k = spec.kill_rule.key;
  const s = [...spec.kill_cards].sort((a, b) => (spec.kill_rule!.dir === 'desc' ? b[k] - a[k] : a[k] - b[k]));
  return s.map((c) => c.id);
}

// ---------------------------------------------------------------------------
// FORM 12-K — CEREMONIAL ARMATURE ALIGNMENT
// Dial end-values are NEVER client-asserted: the server replays the dial
// state machine from hold/release timestamps against seeded sweep rate,
// coupling, and backlash. Value space: % error from the hidden target (0).
// ---------------------------------------------------------------------------

export const DIAL_NAMES_12K = ['ROTATION', 'DROP FORCE', 'INK SATURATION'];

export interface Form12KSpec {
  system: 'FORM12K';
  seed: string;
  grade_tier: number;
  dial_count: number; // 2 (grade 1) or 3
  tolerance_pct: number; // 8 -> 4
  blanks: number; // 6 -> 3
  sweep_rate_pct_s: number; // % per second of hold
  initial_pct: number[]; // seeded starting error per dial
  coupling: { from: number; to: number; factor: number } | null; // grade 4+
  backlash_ms: number | null; // grade 5: dead zone on direction change
  chyron_pool: number; // seeded index for the truthful complaint pool
}

export function gen12K(seed: string, gradeTier: number): Form12KSpec {
  const rnd = streamFrom(seed + ':12k');
  const g = Math.max(1, Math.min(5, gradeTier));
  const dialCount = g <= 1 ? 2 : 3;
  const tolerance = [8, 8, 8, 6, 5, 4][g] || 8;
  const blanks = [6, 6, 6, 5, 4, 3][g] || 6;
  const initial: number[] = [];
  for (let d = 0; d < dialCount; d++) {
    const mag = 18 + rnd() * 26; // 18-44% off
    initial.push((rnd() < 0.5 ? -1 : 1) * Math.round(mag * 10) / 10);
  }
  return {
    system: 'FORM12K',
    seed,
    grade_tier: gradeTier,
    dial_count: dialCount,
    tolerance_pct: tolerance,
    blanks,
    sweep_rate_pct_s: 11 + Math.floor(rnd() * 5), // 11-15 %/s
    initial_pct: initial,
    coupling:
      g >= 4
        ? { from: 1, to: Math.min(2, dialCount - 1), factor: 0.3 }
        : null,
    backlash_ms: g >= 5 ? 220 + Math.floor(rnd() * 180) : null,
    chyron_pool: Math.floor(rnd() * 20),
  };
}

export interface DialEvent12K {
  t: number;
  e: 'dial_hold' | 'dial_release' | 'test_slam' | 'certify' | 'mounted' | 'dismount_abandoned';
  dial?: number;
  dir?: number; // +1 | -1
}

export interface DialReplay12K {
  final_pct: number[];
  test_errors: number[][]; // per test_slam, per dial
  blanks_used: number;
  certified: boolean;
  abandoned: boolean;
  invalid: string | null;
}

/** Full deterministic dial replay — both sides run the identical machine. */
export function replayDials12K(spec: Form12KSpec, events: DialEvent12K[]): DialReplay12K {
  const values = spec.initial_pct.slice();
  const lastDir: number[] = new Array(spec.dial_count).fill(0);
  const holds = new Map<number, { t: number; dir: number }>();
  const tests: number[][] = [];
  let certified = false;
  let abandoned = false;

  const settle = (dial: number, t: number): void => {
    const h = holds.get(dial);
    if (!h) return;
    let dur = t - h.t;
    if (spec.backlash_ms !== null && lastDir[dial] !== 0 && h.dir !== lastDir[dial]) {
      dur = Math.max(0, dur - spec.backlash_ms); // dead zone on direction change
    }
    const delta = (h.dir * spec.sweep_rate_pct_s * dur) / 1000;
    values[dial] += delta;
    if (spec.coupling && spec.coupling.from === dial && spec.coupling.to < spec.dial_count) {
      values[spec.coupling.to] += delta * spec.coupling.factor;
    }
    values[dial] = Math.max(-100, Math.min(100, values[dial]));
    lastDir[dial] = h.dir;
    holds.delete(dial);
  };

  for (const ev of events) {
    if (ev.e === 'dial_hold') {
      const d = Number(ev.dial);
      const dir = Number(ev.dir);
      if (!Number.isInteger(d) || d < 0 || d >= spec.dial_count) {
        return { final_pct: values, test_errors: tests, blanks_used: tests.length, certified, abandoned, invalid: 'a dial that is not installed' };
      }
      if (dir !== 1 && dir !== -1) {
        return { final_pct: values, test_errors: tests, blanks_used: tests.length, certified, abandoned, invalid: 'a sweep in no direction' };
      }
      if (holds.has(d)) {
        return { final_pct: values, test_errors: tests, blanks_used: tests.length, certified, abandoned, invalid: 'a dial held twice at once' };
      }
      holds.set(d, { t: ev.t, dir });
    } else if (ev.e === 'dial_release') {
      const d = Number(ev.dial);
      if (!holds.has(d)) {
        return { final_pct: values, test_errors: tests, blanks_used: tests.length, certified, abandoned, invalid: 'a dial released before it was held' };
      }
      settle(d, ev.t);
    } else if (ev.e === 'test_slam') {
      for (const d of [...holds.keys()]) settle(d, ev.t);
      tests.push(values.map((v) => Math.round(v * 10) / 10));
      if (tests.length > spec.blanks) {
        return { final_pct: values, test_errors: tests, blanks_used: tests.length, certified, abandoned, invalid: 'more test blanks than were issued' };
      }
    } else if (ev.e === 'certify') {
      for (const d of [...holds.keys()]) settle(d, ev.t);
      certified = true;
    } else if (ev.e === 'dismount_abandoned') {
      abandoned = true;
    }
  }
  for (const d of [...holds.keys()]) settle(d, events.length ? events[events.length - 1].t : 0);
  return {
    final_pct: values.map((v) => Math.round(v * 10) / 10),
    test_errors: tests,
    blanks_used: tests.length,
    certified,
    abandoned,
    invalid: null,
  };
}

export function rms(values: number[]): number {
  if (!values.length) return 0;
  return Math.round(Math.sqrt(values.reduce((a, v) => a + v * v, 0) / values.length) * 10) / 10;
}

// ---------------------------------------------------------------------------
// FORM 44-S — STATIONARY COMPOSURE CERTIFICATION
// The beat script is ground truth; drift/excursions recompute from the raw
// route against the hold-start position (0.25m quantization respected).
// ---------------------------------------------------------------------------

export const ANCHORS_44S: Array<{ id: string; wp: string }> = [
  { id: 'MARK-INTAKE', wp: 'intake_c' },
  { id: 'MARK-INTAKE-N', wp: 'intake_n' },
  { id: 'MARK-ROW', wp: 'row_c' },
  { id: 'MARK-ARCHIVE', wp: 'arch_s' },
  { id: 'MARK-GALLERY', wp: 'gallery_e' },
];

export const BEATS_44S = [
  'phone',
  'ghost_stare',
  'lamp_converge',
  'tray_slam',
  'false_release',
  'flicker',
  'whisper',
  'ring_cluster',
] as const;

export interface Form44SSpec {
  system: 'FORM44S';
  seed: string;
  grade_tier: number;
  anchor: string; // ANCHORS_44S id
  duration_s: number; // 30-75
  ring_r: number; // 0.3-0.5
  beats: Array<{ id: string; t_ms: number }>;
  lies: number; // false-release count within beats
  allowance: number; // excursions allowed
}

export function gen44S(seed: string, gradeTier: number): Form44SSpec {
  const rnd = streamFrom(seed + ':44s');
  const g = Math.max(0, Math.min(5, gradeTier));
  const duration = [30, 45, 60, 60, 75, 75][g] || 45;
  const ring = [0.5, 0.45, 0.35, 0.35, 0.3, 0.3][g] || 0.35;
  const allowance = [3, 3, 2, 2, 1, 1][g] || 2;
  const beatCount = [2, 4, 5, 5, 6, 6][g] || 4;
  const lies = g >= 1 ? (g >= 4 ? 2 : 1) : 0;
  const pool = [...BEATS_44S].filter((b) => b !== 'false_release');
  const picks: string[] = [];
  while (picks.length < beatCount - lies && pool.length) {
    picks.push(pool.splice(Math.floor(rnd() * pool.length), 1)[0]);
  }
  for (let i = 0; i < lies; i++) picks.push('false_release');
  // phone + ghost stack from grade 2
  if (g >= 2 && !picks.includes('phone')) picks[0] = 'phone';
  const beats = picks
    .map((id) => ({ id, t_ms: Math.floor((0.12 + rnd() * 0.75) * duration * 1000) }))
    .sort((a, b) => a.t_ms - b.t_ms);
  // false-release lie lands ~60% by canon when present
  const lie = beats.find((b) => b.id === 'false_release');
  if (lie) lie.t_ms = Math.floor(duration * 1000 * 0.6);
  beats.sort((a, b) => a.t_ms - b.t_ms);
  return {
    system: 'FORM44S',
    seed,
    grade_tier: gradeTier,
    anchor: ANCHORS_44S[Math.floor(rnd() * ANCHORS_44S.length)].id,
    duration_s: duration,
    ring_r: ring,
    beats,
    lies,
    allowance,
  };
}

export function anchorCoord44S(anchorId: string): [number, number] {
  const a = ANCHORS_44S.find((x) => x.id === anchorId);
  if (!a) throw new Error(`unknown anchor ${anchorId}`);
  const wp = WAYPOINTS.find((w) => w.id === a.wp)!;
  return [wp.x, wp.y];
}

// ---------------------------------------------------------------------------
// FORM 17-O — OBSERVATION DUTY (shadow + structured deposition)
// The subject's timeline is a pure function of the spec; the server replays
// the player polyline against it (distance + wall-segment line-of-sight)
// to compute the true witnessable set, ignoring every client claim.
// ---------------------------------------------------------------------------

// Interior wall segments in waypoint space, with door gaps left open.
// Derived from the same room boundaries the waypoint graph is authored
// against (see the door-gap comments above WAYPOINTS).
export const WALLS: Array<[number, number, number, number]> = [
  // world x=14 wall (turnstile + gallery arch gaps)
  [17, 3, 17, 7],
  [17, 10, 17, 20],
  [17, 23, 17, 31],
  // world z=6 wall across hall + rotunda (three gaps)
  [17, 11, 22, 11],
  [25, 11, 36, 11],
  [39, 11, 56, 11],
  [58, 11, 61, 11],
  // world x=-12 wall (hall|rotunda + hall|archive gaps)
  [43, 11, 43, 15],
  [43, 19, 43, 27],
  [43, 30, 43, 31],
  // world z=-6 wall (archive|rotunda gap)
  [43, 23, 50, 23],
  [53, 23, 61, 23],
];

function segIntersect(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number
): boolean {
  const d1 = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  const d2 = (bx - ax) * (dy - ay) - (by - ay) * (dx - ax);
  const d3 = (dx - cx) * (ay - cy) - (dy - cy) * (ax - cx);
  const d4 = (dx - cx) * (by - cy) - (dy - cy) * (bx - cx);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

/** Line-of-sight through the authored wall model. Pure, both sides. */
export function losClear(x0: number, y0: number, x1: number, y1: number): boolean {
  for (const [ax, ay, bx, by] of WALLS) {
    if (segIntersect(x0, y0, x1, y1, ax, ay, bx, by)) return false;
  }
  return true;
}

export const ACT_KINDS_17O = [
  'crossed the yellow line',
  'misfiled a folder into drawer 41',
  'loitered in a DO-NOT-STAND zone',
  'pocketed a canister chit',
  'witnessed the same gallery frame twice',
  'adjusted an eye-lamp by hand',
  'read a file addressed to another designation',
  'tapped a terminal that was not assigned',
  'left a form on a bench, unattended',
  'stood at Window 4 without a summons',
] as const;

export interface Act17O {
  id: number;
  kind: number; // index into ACT_KINDS_17O
  path_idx: number; // node on the subject path where it occurs
  irregular: boolean;
  unseeable: boolean; // grade 3+: behind frosted glass — pure honesty test
}

export interface Question17O {
  q_id: number;
  act_id: number | null; // null = the trap (an act that never occurred)
  kind_claim: number; // ACT_KINDS_17O index the punch-card asserts
  false_claim: number; // the wrong-variant option
}

export interface Form17OSpec {
  system: 'FORM17O';
  seed: string;
  grade_tier: number;
  subject_path: string[]; // waypoint walk
  subject_speed_mps: number;
  act_pause_ms: number; // subject pauses this long at each act node
  acts: Act17O[];
  questions: Question17O[];
}

export function gen17O(seed: string, gradeTier: number): Form17OSpec {
  const rnd = streamFrom(seed + ':17o');
  const g = Math.max(2, Math.min(5, gradeTier));
  // seeded lane: walk the graph 10-14 nodes without immediate backtracking
  const start = WAYPOINTS[Math.floor(rnd() * WAYPOINTS.length)];
  const path: string[] = [start.id];
  let cur = start;
  const targetLen = 10 + Math.floor(rnd() * 5);
  let guard = 0;
  while (path.length < targetLen && guard++ < 60) {
    const opts = cur.adj.filter((a) => a !== path[path.length - 2]);
    const next = opts.length ? opts[Math.floor(rnd() * opts.length)] : cur.adj[0];
    path.push(next);
    cur = WAYPOINTS.find((w) => w.id === next)!;
  }
  const actCount = g <= 2 ? 4 : g >= 4 ? 6 : 5;
  const irregularCount = g <= 2 ? 2 : 3;
  // act nodes spread along the path interior
  const nodeIdxs: number[] = [];
  const step = (path.length - 2) / actCount;
  for (let i = 0; i < actCount; i++) nodeIdxs.push(1 + Math.min(path.length - 2, Math.floor(i * step + rnd() * step * 0.8)));
  const kinds = new Set<number>();
  const acts: Act17O[] = nodeIdxs.map((pi, i) => {
    let k = Math.floor(rnd() * ACT_KINDS_17O.length);
    let kguard = 0;
    while (kinds.has(k) && kguard++ < 12) k = (k + 1) % ACT_KINDS_17O.length;
    kinds.add(k);
    return {
      id: i,
      kind: k,
      path_idx: pi,
      irregular: i < irregularCount,
      unseeable: g >= 3 && i === irregularCount - 1 && rnd() < 0.5,
    };
  });
  // questions: about the irregular acts (+ maybe one regular), plus one trap
  const qActs = acts.filter((a) => a.irregular);
  if (g >= 3 && acts.length > irregularCount) qActs.push(acts[irregularCount]);
  let trapKind = Math.floor(rnd() * ACT_KINDS_17O.length);
  let tguard = 0;
  while (kinds.has(trapKind) && tguard++ < 12) trapKind = (trapKind + 1) % ACT_KINDS_17O.length;
  const questions: Question17O[] = qActs.map((a, i) => ({
    q_id: i,
    act_id: a.id,
    kind_claim: a.kind,
    false_claim: (a.kind + 1 + Math.floor(rnd() * (ACT_KINDS_17O.length - 1))) % ACT_KINDS_17O.length,
  }));
  questions.push({
    q_id: questions.length,
    act_id: null, // the honesty trap
    kind_claim: trapKind,
    false_claim: (trapKind + 3) % ACT_KINDS_17O.length,
  });
  return {
    system: 'FORM17O',
    seed,
    grade_tier: gradeTier,
    subject_path: path,
    subject_speed_mps: g <= 2 ? 0.85 : 1.0,
    act_pause_ms: 6000,
    acts,
    questions,
  };
}

export interface SubjectTimeline17O {
  nodes: Array<{ wp: string; x: number; y: number; arrive_ms: number; depart_ms: number }>;
  acts: Array<{ id: number; x: number; y: number; t0: number; t1: number; irregular: boolean; unseeable: boolean; kind: number }>;
  total_ms: number;
}

/** Build the subject's deterministic timeline (walk + act pauses). */
export function subjectTimeline17O(spec: Form17OSpec): SubjectTimeline17O {
  const nodes: SubjectTimeline17O['nodes'] = [];
  let t = 2000; // the subject is acquired 2s in
  let prev: { x: number; y: number } | null = null;
  spec.subject_path.forEach((id, i) => {
    const wp = WAYPOINTS.find((w) => w.id === id)!;
    if (prev) {
      const d = Math.hypot(wp.x - prev.x, wp.y - prev.y);
      t += (d / spec.subject_speed_mps) * 1000;
    }
    const arrive = Math.round(t);
    const pausing = spec.acts.some((a) => a.path_idx === i);
    if (pausing) t += spec.act_pause_ms;
    nodes.push({ wp: id, x: wp.x, y: wp.y, arrive_ms: arrive, depart_ms: Math.round(t) });
    prev = wp;
  });
  const acts = spec.acts.map((a) => {
    const n = nodes[Math.min(a.path_idx, nodes.length - 1)];
    return {
      id: a.id,
      x: n.x,
      y: n.y,
      t0: n.arrive_ms,
      t1: n.arrive_ms + spec.act_pause_ms,
      irregular: a.irregular,
      unseeable: a.unseeable,
      kind: a.kind,
    };
  });
  return { nodes, acts, total_ms: Math.round(t) };
}

/** Subject position at time t (ms) along the timeline. Pure. */
export function subjectPos17O(tl: SubjectTimeline17O, t: number): [number, number] {
  const n = tl.nodes;
  if (!n.length) return [0, 0];
  if (t <= n[0].arrive_ms) return [n[0].x, n[0].y];
  for (let i = 0; i < n.length; i++) {
    if (t >= n[i].arrive_ms && t <= n[i].depart_ms) return [n[i].x, n[i].y];
    if (i + 1 < n.length && t > n[i].depart_ms && t < n[i + 1].arrive_ms) {
      const f = (t - n[i].depart_ms) / (n[i + 1].arrive_ms - n[i].depart_ms);
      return [n[i].x + (n[i + 1].x - n[i].x) * f, n[i].y + (n[i + 1].y - n[i].y) * f];
    }
  }
  return [n[n.length - 1].x, n[n.length - 1].y];
}

// ---------------------------------------------------------------------------
// Union + issue helper
// ---------------------------------------------------------------------------

export type PhysicalTaskSpec =
  | Form5JSpec
  | Form2VSpec
  | Form33CSpec
  | Form00Spec
  | Form8RSpec
  | Form5DSpec
  | Form5MSpec
  | Form5SSpec
  | Form4TSpec
  | Form12KSpec
  | Form44SSpec
  | Form17OSpec;

/** Deterministic seed for a docket slot: (docket_id, idx, petitioner). */
export function slotSeed(docketId: string, idx: number, petitionerId: string): string {
  return `${docketId}:${idx}:${fnv1a(petitionerId + docketId).toString(36)}`;
}

/** Generate the pure-seeded part of a task spec (5-J tray filled server-side). */
export function generateSpec(
  system: PhysicalSystemId,
  seed: string,
  gradeTier: number,
  opts?: { penance?: boolean }
): PhysicalTaskSpec {
  switch (system) {
    case 'FORM5J':
      return gen5J(seed, gradeTier);
    case 'FORM2V':
      return gen2V(seed, gradeTier);
    case 'FORM33C':
      return gen33C(seed, gradeTier, opts?.penance === true);
    case 'FORM00':
      return gen00(seed, gradeTier);
    case 'FORM8R':
      return gen8R(seed, gradeTier);
    case 'FORM5D':
      return gen5D(seed, gradeTier);
    case 'FORM5M':
      return gen5M(seed, gradeTier);
    case 'FORM5S':
      return gen5S(seed, gradeTier);
    case 'FORM4T':
      return gen4T(seed, gradeTier);
    case 'FORM12K':
      return gen12K(seed, gradeTier);
    case 'FORM44S':
      return gen44S(seed, gradeTier);
    case 'FORM17O':
      return gen17O(seed, gradeTier);
    default:
      throw new Error(`system ${system} has no registered generator`);
  }
}
